import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { preRegistrationApi } from '../api/pre-registration.api';
import type {
  MunicipalListSearchParams,
  PreRegisteredAccommodationSearchParams,
} from '../types/pre-registration.types';

// Query keys shared by every pre-registration screen (list pages, form, lookup)
export const preRegistrationKeys = {
  all: ['pre-registrations'] as const,
  accommodations: () => [...preRegistrationKeys.all, 'accommodations'] as const,
  accommodationList: (params: PreRegisteredAccommodationSearchParams) =>
    [...preRegistrationKeys.accommodations(), 'list', params] as const,
  accommodationDetail: (id: string) =>
    [...preRegistrationKeys.accommodations(), 'detail', id] as const,
  statuses: () => [...preRegistrationKeys.all, 'statuses'] as const,
  municipalLists: () => [...preRegistrationKeys.all, 'municipal-lists'] as const,
  municipalListList: (params: MunicipalListSearchParams) =>
    [...preRegistrationKeys.municipalLists(), 'list', params] as const,
  municipalListDetail: (id: string) =>
    [...preRegistrationKeys.municipalLists(), 'detail', id] as const,
  municipalities: (departmentId?: number) =>
    [...preRegistrationKeys.all, 'municipalities', departmentId ?? 'all'] as const,
  municipalityLocalities: (municipalityId: number) =>
    [...preRegistrationKeys.all, 'municipality-localities', municipalityId] as const,
  officialLookup: (q: string) => [...preRegistrationKeys.all, 'official-lookup', q] as const,
};

export const usePreRegisteredAccommodations = (params: PreRegisteredAccommodationSearchParams) => {
  return useQuery({
    queryKey: preRegistrationKeys.accommodationList(params),
    queryFn: () => preRegistrationApi.searchAccommodations(params),
    // Keep the current page visible while the next one loads
    placeholderData: keepPreviousData,
  });
};

export const usePreRegistrationStatuses = () => {
  return useQuery({
    queryKey: preRegistrationKeys.statuses(),
    queryFn: preRegistrationApi.getStatuses,
    staleTime: Infinity,
  });
};

export const useMunicipalLists = (params: MunicipalListSearchParams = {}) => {
  return useQuery({
    queryKey: preRegistrationKeys.municipalListList(params),
    queryFn: () => preRegistrationApi.searchMunicipalLists(params),
    // Keep the current rows visible while a filter change loads
    placeholderData: keepPreviousData,
  });
};
